import React, { useRef } from "react";
import Main from '../../Main/components'
import { EXTERNAL_LINK_DESCRIPTOR, GRAD_SCHOOL_FAX_NUMBER, GRAD_SCHOOL_PHONE_NUMBER, MAIN_ID, OPENS_NEW_WINDOW_DESCRIPTOR, SUPPORT_EMAIL } from "../../util/constants";
import Footer from "@osu/footer";
import { withOSUTheming, useScrollToHash, Mui, MuiStyles, withTableOfContents, useFooterWithTOC, withDarkMode } from '@osu/react-ui';
import { AppProvider } from "../context";
import withAmplifyCacheValues from "../hocs/withAmplifyCacheValues";
import { navOverrideStyles } from "../overrides";
import { Navbar as OSUNavBar } from "@osu/navbar";
import useGridSizing from "../../Dashboard/hooks/useGridSizing";
import Navbar from "../../Navbar/containers";
import { mergeSxStyles } from "../../styles/util";
import usePageTrackingWithObserver from "../../Analytics/hooks/usePageTrackingWithObserver";
import withAuthentication from "../../Authentication/withAuthentication";
import useCssVariableUpdater from "../hooks/useCssVariableUpdater";

function App(props = {}) {
  const { navHeight, darkMode, FooterProps, theme } = props
  const { ref } = useScrollToHash()
  const footerRef = useRef()
  useFooterWithTOC({ ref: footerRef })
  const { padding } = useGridSizing()
  const trigger = Mui.useScrollTrigger({
    disableHysteresis: true,
    threshold: navHeight
  });
  usePageTrackingWithObserver();

  const sx = mergeSxStyles(
    (theme) => navOverrideStyles(theme, padding),
    (theme)=> ({
      [` .${Mui.linkClasses.root}`]: {
        fontWeight: theme.typography.fontWeightBold
      }
    })
  );

  const footerProps = {
    address: {
        orgName: "Graduate School",
        addressLine1: "250 University Hall",
        addressLine2: "230 N. Oval Mall",
        city: "Columbus",
        state: "OH",
        zip: "43210"
    },
    ContactProps: {
      hideEmail: true,
      hidePhone: true,
      children: (
        <Mui.Box alignItems="flex-start" display="flex" flexDirection="column">
          <Mui.Box><Mui.Typography variant="body2">Phone: <Mui.Link href={`tel:${GRAD_SCHOOL_PHONE_NUMBER}`}>{GRAD_SCHOOL_PHONE_NUMBER}</Mui.Link></Mui.Typography></Mui.Box>
          <Mui.Box><Mui.Typography variant="body2">Fax: <Mui.Link href={`tel:${GRAD_SCHOOL_FAX_NUMBER}`}>{GRAD_SCHOOL_FAX_NUMBER}</Mui.Link></Mui.Typography></Mui.Box>
          <Mui.Box><Mui.Typography variant="body2">Graduation Services: <Mui.Link href={`mailto:${SUPPORT_EMAIL.GRADUATION_SERVICES}`}>{SUPPORT_EMAIL.GRADUATION_SERVICES}</Mui.Link></Mui.Typography></Mui.Box>
          <Mui.Box><Mui.Typography variant="body2">Registration Services: <Mui.Link href={`mailto:${SUPPORT_EMAIL.REGISTRATION_SERVICES}`}>{SUPPORT_EMAIL.REGISTRATION_SERVICES}</Mui.Link></Mui.Typography></Mui.Box>
        </Mui.Box>
      )
    },
    darkMode,
    ref: footerRef
  }
  return (
    <Mui.ThemeProvider theme={theme}>
      <Mui.Box className="App" ref={ref} sx={sx}>
        <OSUNavBar darkMode={darkMode} />
        <Navbar darkMode={darkMode} />
        <Main className="anchor-link" spacingTop={!trigger} id={MAIN_ID} />
        <Footer {...footerProps} {...FooterProps ?? {}} />
        <Mui.Typography id={EXTERNAL_LINK_DESCRIPTOR} aria-hidden variant="srOnly">&nbsp;(link is external)</Mui.Typography>
        <Mui.Typography id={OPENS_NEW_WINDOW_DESCRIPTOR} aria-hidden variant="srOnly">&nbsp;(opens new window)</Mui.Typography>
      </Mui.Box>
    </Mui.ThemeProvider>
  );
}

const TOCWrappedApp = withTableOfContents(App)

function Provider(props = {}) {
  const { authorized, handleSignIn, authStatus, handleSignOut, darkMode, external, isLoggedIn, FooterProps } = props;
  const { navHeight } = useCssVariableUpdater()
  const theme = MuiStyles.useTheme();

  const paletteMode = (darkMode ? "dark": "light");
  if(theme.palette.mode !== paletteMode) theme.palette.mode = paletteMode;
  if(theme.palette.mode === "dark") {
    theme.components = {
      MuiAlert: {
        styleOverrides: {
          root: {
            border: "1px solid"
          }
        }
      },
      MuiTableCell: {
        root: {
          color: "#fff"
        }
      }
    }
  }
  const { palette } = theme;

  const appContext = {
    authStatus,
    authorized,
    handleSignIn,
    handleSignOut,
    navHeight,
    darkMode,
    external,
    isLoggedIn,
    palette
  }

  
  return (
    <AppProvider value={appContext}>
      <TOCWrappedApp darkMode={darkMode} navHeight={navHeight} FooterProps={FooterProps} theme={theme} />
    </AppProvider>
  );
}

export default withAmplifyCacheValues(withDarkMode(withOSUTheming(withAuthentication(Provider))));